import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, HeartHandshake, BookOpen, HandHeart, Radio, Quote } from "lucide-react";
import Counter from "@/components/Counter";
import SectionEyebrow from "@/components/SectionEyebrow";
import { IMAGES, DEPARTMENTS, VISION_CARDS } from "@/data/content";
import api from "@/lib/api";

const ICONS = { Megaphone, HeartHandshake, BookOpen, HandHeart, Radio };

const Home = () => {
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [tIndex, setTIndex] = useState(0);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTests(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tests.length === 0) return;
    const id = setInterval(() => setTIndex((i) => (i + 1) % tests.length), 6000);
    return () => clearInterval(id);
  }, [tests.length]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Ministère MIREVSA en action"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Soft dark blue overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3D91]/70 via-[#0B3D91]/60 to-[#0B3D91]/80" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-32 text-white text-center">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="h-px w-12 bg-white/80" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-white">
                Ministère Chrétien · Haïti
              </span>
              <span className="h-px w-12 bg-white/80" />
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Transformer des vies <br />
              par la puissance <br />
              de l'Évangile.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-blue-50/95 max-w-2xl mx-auto leading-relaxed">
              MIREVSA est une organisation chrétienne engagée dans l'évangélisation,
              la formation et l'impact social à l'échelle nationale et internationale.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/join" data-testid="hero-join-btn" className="btn-primary">
                Rejoindre la mission <ArrowRight size={18} />
              </Link>
              <Link to="/donation" data-testid="hero-donate-btn" className="bg-white text-[#0B3D91] hover:bg-blue-50 inline-flex items-center justify-center gap-2 px-7 py-3.5 font-bold transition-all duration-200">
                Faire un don
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="mv-section bg-white" data-testid="home-about-section">
        <div className="mv-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionEyebrow>Notre identité</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
              Une mission claire.<br />Un impact durable.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-gray-200">
            <p className="text-lg text-muted leading-relaxed">
              <span className="font-bold text-[#1C1C1C]">MIREVSA</span> — Ministère Réveil d'Évangélisation pour
              le Salut des Âmes — est une organisation chrétienne dédiée à l'évangélisation massive,
              à la formation biblique, à la prière stratégique et à l'impact social.
            </p>
            <p className="mt-5 text-lg text-muted leading-relaxed">
              Depuis Haïti, nous portons l'Évangile dans les rues, équipons des leaders et servons
              les communautés les plus vulnérables.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="mt-8 inline-flex items-center gap-2 font-bold text-[#0B3D91] hover:gap-3 transition-all">
              En savoir plus sur nous <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* UNE VISION, UNE MISSION */}
      <section className="mv-section bg-[#F8FAFC]" data-testid="home-vision-section">
        <div className="mv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <SectionEyebrow>Qui sommes-nous</SectionEyebrow>
              <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
                Une vision, une mission.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base text-muted leading-relaxed">
                MIREVSA est engagée dans une vision d'évangélisation complète — associant transformation
                spirituelle et développement humain. Par la Parole, la prière, la formation et les actions
                sociales, nous restaurons la dignité et bâtissons des communautés plus solides.
              </p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 font-bold text-[#0B3D91] hover:gap-3 transition-all">
                Découvrir notre histoire <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {VISION_CARDS.map((v, i) => (
              <figure
                key={i}
                data-testid={`vision-image-${i + 1}`}
                className={`group overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 aspect-[4/5] ${
                  // Subtle vertical offset for a balanced, editorial rhythm on large screens
                  i % 2 === 1 ? "lg:translate-y-8" : ""
                }`}
              >
                <img
                  src={v.image}
                  alt={v.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* DEPARTMENTS */}
      <section className="mv-section bg-[#F8FAFC]" data-testid="home-departments-section">
        <div className="mv-container">
          <div className="max-w-2xl">
            <SectionEyebrow>Nos départements</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C]">
              Au cœur de notre action.
            </h2>
            <p className="mt-5 text-base text-muted leading-relaxed">
              Quatre départements principaux portent notre mission vers les communautés du monde entier.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {DEPARTMENTS.filter((d) => d.slug !== "communication").map((d) => {
              const Icon = ICONS[d.icon];
              return (
                <Link
                  key={d.slug}
                  to={`/departments#${d.slug}`}
                  data-testid={`dept-card-${d.slug}`}
                  className="card-flat group block"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={d.image}
                      alt={d.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 h-11 w-11 bg-white flex items-center justify-center rounded-md">
                      <Icon size={20} className="text-[#0B3D91]" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-[#1C1C1C]">{d.title}</h3>
                    <p className="mt-2 text-sm text-muted leading-relaxed">{d.short}</p>
                    <div className="mt-4 text-sm font-bold text-[#0B3D91] flex items-center gap-2 group-hover:gap-3 transition-all">
                      Découvrir <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section
        className="relative py-24 bg-[#0B3D91] text-white overflow-hidden"
        data-testid="home-stats-section"
      >
        <div className="absolute inset-0 opacity-[0.08]">
          <img src={IMAGES.outreachTeam} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative mv-container">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-white/80" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold">Notre impact</span>
            </div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">
              Des chiffres qui parlent.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { label: "Vies transformées", value: stats?.souls_touched ?? 600, suffix: "+" },
              { label: "Communautés touchées", value: stats?.missions_completed ?? 40, suffix: "+" },
              { label: "Missions actives", value: stats?.churches_supported ?? 15, suffix: "+" },
              { label: "Leaders formés", value: stats?.social_actions ?? 80, suffix: "+" },
            ].map((s, i) => (
              <div key={s.label} data-testid={`stat-${i}`}>
                <div className="font-heading font-black text-5xl sm:text-6xl tracking-tight">
                  <Counter end={s.value} suffix={s.suffix || ""} />
                </div>
                <div className="mt-3 text-blue-100 text-sm uppercase tracking-[0.15em] font-medium">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIES */}
      <section className="mv-section bg-white" data-testid="home-testimonies-section">
        <div className="mv-container">
          <div className="max-w-2xl mb-12">
            <SectionEyebrow>Témoignages</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C]">
              Des vies transformées.
            </h2>
          </div>

          {tests.length > 0 && (
            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-5">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={tests[tIndex].image_url || IMAGES.prayingHands}
                      alt={tests[tIndex].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <Quote size={48} className="text-[#0B3D91]" />
                  <blockquote className="mt-6 font-heading text-2xl sm:text-3xl text-[#1C1C1C] leading-tight font-medium">
                    « {tests[tIndex].quote} »
                  </blockquote>
                  <div className="mt-8">
                    <div className="font-bold text-[#1C1C1C]">{tests[tIndex].name}</div>
                    <div className="text-sm text-muted">{tests[tIndex].location}</div>
                  </div>

                  <div className="mt-10 flex gap-2">
                    {tests.map((_, i) => (
                      <button
                        key={i}
                        data-testid={`testimony-dot-${i}`}
                        onClick={() => setTIndex(i)}
                        aria-label={`Témoignage ${i + 1}`}
                        className={`h-1.5 transition-all ${
                          i === tIndex ? "w-10 bg-[#0B3D91]" : "w-5 bg-gray-300 hover:bg-gray-400"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* COMMUNITY IMPACT (Featured image) */}
      <section className="relative" data-testid="home-community-section">
        <div className="relative w-full h-[70vh] min-h-[500px] overflow-hidden">
          <img
            src={IMAGES.footerImage}
            alt="Communauté MIREVSA en action"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B3D91]/85 via-[#0B3D91]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="mv-container text-white">
              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-white/80" />
                  <span className="text-xs uppercase tracking-[0.3em] font-bold">Impact communautaire</span>
                </div>
                <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  Au service de nos communautés.
                </h2>
                <p className="mt-6 text-lg text-blue-50/95 leading-relaxed">
                  Chaque visage, chaque famille, chaque quartier — MIREVSA se tient aux côtés
                  des Haïtiens avec l'amour du Christ, la Parole de vérité et l'aide concrète.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-28 bg-[#1C1C1C]" data-testid="home-final-cta">
        <div className="mv-container text-center text-white">
          <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl mx-auto">
            La moisson est grande. Serez-vous l'ouvrier ?
          </h2>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
            Rejoignez MIREVSA — devenez membre, volontaire, ou soutenez la mission par un don.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/join" className="bg-white text-[#1C1C1C] hover:bg-gray-100 inline-flex items-center justify-center gap-2 px-7 py-3.5 font-bold transition-all">
              Rejoindre la mission
            </Link>
            <Link to="/donation" className="btn-primary">
              Faire un don
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
